import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  CalendarX,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock,
  ExternalLink,
  FilePlus2,
  Folder,
  GraduationCap,
  Hourglass,
  LineChart,
  Plus,
  TrendingUp,
  Zap,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useAuthStore } from "@/store/authStore";
import { PageSkeleton } from "@/components/shared/Skeletons";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/forms";
import { cn } from "@/lib/utils";
import { useStudentDashboard, useSubmitWorkLog } from "@/hooks/useStudentDashboard";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/student/dashboard/")({ component: StudentDashboard });

type Tone = "default" | "success" | "warning" | "destructive";

const toneText: Record<Tone, string> = {
  default: "text-muted-foreground",
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
};

function StatCard({
  label,
  value,
  suffix,
  status,
  tone = "default",
  icon,
  progress,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  status?: string;
  tone?: Tone;
  icon: React.ReactNode;
  progress?: number;
}) {
  return (
    <div className="flex h-full min-w-0 flex-col gap-3 rounded-md border border-border bg-card p-4 transition-colors hover:border-hairline-strong md:p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="eyebrow min-w-0 text-pretty">{label}</p>
        <span
          className="flex size-8 shrink-0 lg:max-2xl:hidden items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
          aria-hidden
        >
          {icon}
        </span>
      </div>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
        <span className="text-[28px] leading-9 font-normal tracking-[-0.8px] text-foreground tabular-nums">
          {value}
        </span>
        {suffix && <span className="text-caption text-muted-foreground">{suffix}</span>}
      </div>
      {status && <p className={cn("text-caption font-medium", toneText[tone])}>{status}</p>}
      {typeof progress === "number" && (
        <Progress value={progress} aria-label={`${label} progress`} />
      )}
    </div>
  );
}

function StudentDashboard() {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [logForm, setLogForm] = useState({
    date: new Date().toISOString().split("T")[0],
    hours: "",
    description: "",
    learning: "",
  });

  const { data: dashboard, isLoading } = useStudentDashboard();
  const submitLog = useSubmitWorkLog();

  if (isLoading) {
    return <PageSkeleton />;
  }

  const dash = dashboard as any;
  const stats: any = dash?.stats || {
    total_hours: 0,
    approved_logs: 0,
    pending_logs: 0,
    days_remaining: 0,
  };
  const placement: any = dash?.placement || null;
  const recentLogs: any[] = dash?.recent_logs || [];
  const productivity = dash?.productivity || [];
  const hoursRequired = Number(placement?.hours_required || 0);
  const progress = placement
    ? Math.min(Math.round((stats.total_hours / Math.max(hoursRequired, 1)) * 100), 100)
    : 0;

  const productivityData = Array.isArray(productivity) ? productivity : [];

  const totalThisWeek = productivityData.reduce((sum: number, d: any) => sum + (d.hours || 0), 0);
  const workingDays = productivityData.filter((d: any) => (d.hours || 0) > 0).length || 1;
  const dailyAvg = totalThisWeek / workingDays;
  const bestDay = [...productivityData].sort(
    (a: any, b: any) => (b.hours || 0) - (a.hours || 0),
  )[0];

  const deadlines: any[] = Array.isArray(dash?.deadlines) ? dash.deadlines : [];

  const activities: any[] = Array.isArray(dash?.activities) ? dash.activities : [];

  const handleLogWork = async () => {
    try {
      await submitLog.mutateAsync({
        date: logForm.date,
        hours: parseFloat(logForm.hours),
        description: `${logForm.description}\n\nLearning: ${logForm.learning}`,
        challenge: "None reported",
      });
      setOpen(false);
      setLogForm({
        date: new Date().toISOString().split("T")[0],
        hours: "",
        description: "",
        learning: "",
      });
    } catch (err) {
      console.error("Failed to submit work log:", err);
    }
  };

  const quickActions = [
    {
      icon: <Clock />,
      title: "Log work hours",
      subtitle: "Record your daily hours",
      onClick: () => setOpen(true),
      to: undefined as string | undefined,
    },
    {
      icon: <GraduationCap />,
      title: "Browse courses",
      subtitle: "Explore available courses",
      to: "/student/courses",
    },
    {
      icon: <LineChart />,
      title: "View progress",
      subtitle: "Track your milestones",
      to: "/student/placement",
    },
    {
      icon: <Folder />,
      title: "My documents",
      subtitle: "Access your files",
      to: "/student/reports",
    },
  ];

  const statusVariant: Record<string, { variant: "success" | "warning" | "destructive"; label: string }> = {
    approved: { variant: "success", label: "Approved" },
    pending: { variant: "warning", label: "Pending" },
    rejected: { variant: "destructive", label: "Rejected" },
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0] || "Student"}`}
        subtitle="Here's what's happening with your internship journey."
        action={
          <>
            <Button variant="outline" asChild>
              <Link to="/student/courses">Browse courses</Link>
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus />
              Log work hours
            </Button>
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Left column */}
        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:gap-4 xl:grid-cols-4">
            <StatCard
              label="Total hours"
              value={stats.total_hours}
              suffix={hoursRequired ? `/ ${hoursRequired} hrs` : "logged"}
              status={`${progress}% Completed`}
              icon={<Clock />}
              progress={progress}
            />
            <StatCard
              label="Approved logs"
              value={stats.approved_logs}
              status="Verified"
              tone="success"
              icon={<CheckCircle2 />}
            />
            <StatCard
              label="Pending approval"
              value={stats.pending_logs}
              status="Awaiting Review"
              tone="warning"
              icon={<Hourglass />}
            />
            <StatCard
              label="Days remaining"
              value={stats.days_remaining}
              status="Critical"
              tone="destructive"
              icon={<CalendarX />}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
            {/* Productivity */}
            <SectionCard
              title="Productivity overview"
              description="Your working hours distribution this week"
              actions={
                <Badge variant="success">
                  <TrendingUp />
                  +12.5%
                </Badge>
              }
            >
              <div className="relative flex h-48 items-end gap-2 pb-1">
                <div
                  className="absolute top-0 bottom-6 left-0 flex w-6 flex-col justify-between"
                  aria-hidden
                >
                  {["12h", "8h", "4h", "0h"].map((y) => (
                    <span key={y} className="text-[10px] text-muted-foreground tabular-nums">
                      {y}
                    </span>
                  ))}
                </div>

                {productivityData.length > 0 ? (
                  <div className="ml-8 flex h-full flex-1 items-end justify-between gap-2">
                    {productivityData.map((day: any, i: number) => {
                      const hours = day.hours || 0;
                      const heightPct = Math.max((hours / 12) * 100, hours > 0 ? 4 : 0);
                      const isWeekend = day.day === "Sat" || day.day === "Sun";
                      return (
                        <div
                          key={i}
                          className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                        >
                          <span
                            className={cn(
                              "text-[10.5px] tabular-nums",
                              hours > 0 ? "text-foreground" : "text-muted-foreground",
                            )}
                          >
                            {hours > 0 ? `${hours.toFixed(1)}h` : "—"}
                          </span>
                          <div
                            className={cn(
                              "w-full max-w-8 rounded-t-sm transition-opacity hover:opacity-85",
                              isWeekend ? "bg-hairline" : "bg-chart-1",
                            )}
                            style={{
                              height: `${heightPct}%`,
                              minHeight: hours > 0 ? 12 : 0,
                            }}
                          />
                          <span className="font-mono text-[10.5px] tracking-wide text-muted-foreground uppercase">
                            {(day.day || "").slice(0, 3)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ml-8 grid h-full flex-1 place-items-center">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-md border border-border bg-background text-muted-foreground">
                        <LineChart className="size-5" aria-hidden />
                      </div>
                      <p className="max-w-xs text-sm text-muted-foreground">
                        No productivity data yet. Approved work logs will appear here.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <dl className="mt-5 grid grid-cols-3 gap-4 border-t border-border pt-4">
                <div>
                  <dt className="eyebrow">Total this week</dt>
                  <dd className="mt-1 text-base text-foreground tabular-nums">
                    {totalThisWeek.toFixed(1)} hrs
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow">Daily average</dt>
                  <dd className="mt-1 text-base text-foreground tabular-nums">
                    {dailyAvg.toFixed(1)} hrs
                  </dd>
                </div>
                <div>
                  <dt className="eyebrow">Best day</dt>
                  <dd className="mt-1 text-base text-foreground">{bestDay?.day || "—"}</dd>
                </div>
              </dl>
            </SectionCard>

            {/* Placement */}
            <section className="flex flex-col rounded-md border border-border bg-card p-4 md:p-5">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex size-11 items-center justify-center overflow-hidden rounded-md border border-border bg-background text-body">
                  {placement?.partner_logo ? (
                    <img
                      src={placement.partner_logo}
                      alt={placement.partner_name}
                      className="size-full object-cover"
                    />
                  ) : (
                    <Briefcase className="size-5" aria-hidden />
                  )}
                </div>
                <Button variant="ghost" size="sm" asChild className="-mr-2">
                  <Link to="/student/placement">
                    View details
                    <ChevronRight />
                  </Link>
                </Button>
              </div>
              <h2 className="text-base leading-6 font-medium text-foreground">
                {placement?.partner_name || "No active placement yet"}
              </h2>
              <p className="mt-0.5 mb-5 text-sm text-muted-foreground">
                {placement?.role || placement?.slot_title || "Published placement data will appear here"}
              </p>

              <div className="mt-auto">
                <div className="mb-2 flex justify-between text-caption">
                  <span className="text-muted-foreground">Program milestone</span>
                  <span className="text-foreground tabular-nums">{progress}%</span>
                </div>
                <Progress value={progress} aria-label="Program milestone" className="mb-4" />
                <p className="mb-4 text-caption leading-5 text-muted-foreground italic">
                  {placement?.supervisor_note ||
                    placement?.feedback ||
                    "No supervisor note has been recorded for this account yet."}
                </p>
              </div>

              <Button variant="outline" className="w-full" asChild>
                <Link to="/student/placement">
                  Placement portal
                  <ExternalLink />
                </Link>
              </Button>
            </section>
          </div>

          {/* Quick actions */}
          <SectionCard title="Quick actions">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 2xl:grid-cols-4">
              {quickActions.map((action) => {
                const body = (
                  <>
                    <span
                      className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
                      aria-hidden
                    >
                      {action.icon}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground">
                        {action.title}
                      </span>
                      <span className="block truncate text-caption text-muted-foreground">
                        {action.subtitle}
                      </span>
                    </span>
                  </>
                );
                const cls =
                  "flex w-full items-center gap-3 rounded-md border border-border bg-card p-3 text-left transition-colors hover:border-hairline-strong hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
                if (action.to) {
                  return (
                    <Link key={action.title} to={action.to} className={cls}>
                      {body}
                    </Link>
                  );
                }
                return (
                  <button key={action.title} type="button" onClick={action.onClick} className={cls}>
                    {body}
                  </button>
                );
              })}
            </div>
          </SectionCard>

          {/* Recent work logs */}
          <SectionCard
            title="Recent work logs"
            noPadding
            actions={
              <Button variant="ghost" size="sm" asChild>
                <Link to="/student/work-logs">
                  View all
                  <ChevronRight />
                </Link>
              </Button>
            }
          >
            {recentLogs.length > 0 ? (
              <ul className="divide-y divide-border">
                {recentLogs.slice(0, 5).map((log: any, idx: number) => {
                  const status = statusVariant[log.status as string] || statusVariant.pending;
                  return (
                    <li key={log.id || idx}>
                      <Link
                        to={`/student/work-logs/${log.id}`}
                        className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3 transition-colors hover:bg-accent md:grid-cols-[120px_1fr_64px_96px] md:px-5"
                      >
                        <span className="hidden text-caption text-muted-foreground tabular-nums md:block">
                          {log.date
                            ? new Date(log.date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </span>
                        <span className="min-w-0 truncate text-sm text-foreground">
                          {log.description || "No description"}
                        </span>
                        <span className="hidden text-right font-mono text-sm text-body tabular-nums md:block">
                          {log.hours ? `${log.hours}h` : "—"}
                        </span>
                        <span className="text-right">
                          <Badge variant={status.variant} dot>
                            {status.label}
                          </Badge>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                bare
                icon={<FilePlus2 />}
                title="No work logs yet"
                description="Start logging your hours to track your progress."
                actionLabel="Log your first entry"
                onAction={() => setOpen(true)}
              />
            )}
          </SectionCard>
        </div>

        {/* Right column */}
        <div className="flex min-w-0 flex-col gap-4">
          <SectionCard
            title="Upcoming deadlines"
            actions={<CalendarDays className="size-4 text-muted-foreground" aria-hidden />}
          >
            {deadlines.length > 0 ? (
              <ul className="space-y-3.5">
                {deadlines.map((d: any, i: number) => (
                  <li key={d.id || i} className="flex items-start gap-3">
                    <span
                      className="mt-1.5 size-2 shrink-0 rounded-full bg-warning"
                      style={d.color ? { backgroundColor: d.color } : undefined}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {d.label || d.title || "Deadline"}
                      </p>
                      <p className="text-caption text-muted-foreground">
                        {d.due_in || d.due_date || "Date pending"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No upcoming deadlines from your account yet.
              </p>
            )}
            {deadlines.length > 0 && (
              <Button variant="ghost" size="sm" className="mt-4 w-full">
                View all deadlines
                <ArrowRight />
              </Button>
            )}
          </SectionCard>

          <SectionCard
            title="Recent activities"
            actions={<Zap className="size-4 text-muted-foreground" aria-hidden />}
          >
            {activities.length > 0 ? (
              <ul className="space-y-3.5">
                {activities.map((a: any, i: number) => {
                  const Icon = a.icon === "check" ? ClipboardCheck : Zap;
                  return (
                    <li key={a.id || i} className="flex items-start gap-3">
                      <span
                        className="flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-background text-body"
                        aria-hidden
                      >
                        <Icon className="size-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {a.label || a.title || "Activity"}
                        </p>
                        <p className="text-caption text-muted-foreground">
                          {a.time || a.created_at || ""}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No recent activity from your account yet.
              </p>
            )}
            {activities.length > 0 && (
              <Button variant="ghost" size="sm" className="mt-4 w-full">
                View all activities
                <ArrowRight />
              </Button>
            )}
          </SectionCard>
        </div>
      </div>

      {/* Work log dialog */}
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Daily industrial progress log</DialogTitle>
            <DialogDescription>
              Document your professional activities and hours performed during your placement.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Execution date">
              <DatePicker
                value={logForm.date}
                onChange={(v) => setLogForm((p) => ({ ...p, date: v }))}
              />
            </Field>
            <Field label="Hours logged">
              <Input
                type="number"
                placeholder="0"
                value={logForm.hours}
                onChange={(e) => setLogForm((p) => ({ ...p, hours: e.target.value }))}
              />
            </Field>
            <Field label="Detailed activity description" className="sm:col-span-2">
              <Textarea
                rows={4}
                placeholder="What technical tasks did you perform today?"
                value={logForm.description}
                onChange={(e) => setLogForm((p) => ({ ...p, description: e.target.value }))}
              />
            </Field>
            <Field label="Key learning / outcome" className="sm:col-span-2">
              <Input
                placeholder="Describe one thing you mastered today"
                value={logForm.learning}
                onChange={(e) => setLogForm((p) => ({ ...p, learning: e.target.value }))}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleLogWork} loading={submitLog.isPending}>
              Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
